/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useRef, useState, useEffect } from 'react';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';
import { func, bool } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/styles';
import { Drawer as MuiDrawer } from '@material-ui/core';
import { getSSRDeviceType } from '../../../state/ducks/App/App-Selectors';
import { getBrand } from '../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getProfileInfo } from '../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getAccessTokenSafely } from '../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { useAuth } from '../../../state/ducks/Member/ducks/Auth/Plugin/auth';
import { getFeatureFlag } from '../../../state/ducks/App/ducks/Config/Config-Selectors';
import { trackEvent } from '../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    paper: {
        width: '450px',
        borderTopLeftRadius: '12px',
        borderBottomLeftRadius: '12px',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: '90vh',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            borderBottomLeftRadius: '0px',
            borderBottomRightRadius: '0px',
        },
    },
    backdrop: {
        zIndex: '9999 !important',
        '& .MuiBackdrop-root': {
            opacity: '0.25 !important',
        },
    },
    container: {
        width: 'auto',
        height: '100vh',
        boxShadow: 'none',
        border: 'none',
    },
}));

const RemindersDrawerContainer = ({ open, onClose }) => {
    const dispatch = useDispatch();
    // Styles
    const theme = useTheme();
    const classes = useStyles();
    const { loginByRedirect } = useAuth();

    // Selectors
    const isUsingAppDirectOriginUrl = useSelector(getFeatureFlag('is-using-app-direct-origin-url'));
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const userInfo = useSelector(getProfileInfo);
    const brand = useSelector(getBrand);

    // State & Refs
    const iframeRef = useRef(null);
    const [isIframeLoaded, setIsIframeLoaded] = useState(true);
    const getSessionStorageRemindersData = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('guestRemindersAnywhereData');

    let trustedOrigin = `${mbpUtil.getEnv('APP_MBP_ENGAGEMENT_HOST')}`;
    if (isUsingAppDirectOriginUrl) {
        trustedOrigin = `https://${mbpUtil.getEnv('APP_DIRECT_ORIGIN_URL')}`;
    }

    const isMobile = ssrDeviceType === 'mobile';

    const handleAuthRedirect = (userResponse, guestRemindersAnywhereData) => {
        sessionStorage.setItem('guestRemindersAnywhereData', JSON.stringify({ ...guestRemindersAnywhereData }));
        const options = {
            login_hint: userResponse.email,
        };
        if (userResponse.code === 200 || userResponse.code === 412) {
            options['connection'] = 'email';
            loginByRedirect(options, { register: 'N' });
        } else {
            loginByRedirect(options);
        }
    };

    useEffect(() => {
        /* Event types:
            - REQUEST_CLOSE_DRAWER

            - REQUEST_USER_PROFILE
            - RESPONSE_USER_PROFILE

            - REQUEST_TOKEN_REFRESH
            - RESPONSE_TOKEN_REFRESH

            - REQUEST_THEME
            - RESPONSE_THEME

            - REQUEST_AUTH_REDIRECT
            - REQUEST_CLEAR_REMINDERS_SESSION_DATA
        */
        const handleiFrameMessage = (event) => {
            try {
                const eventType = event.data?.type || '';

                // Registered User - send user profile to iFrame
                // note: rename this to user session as more props come in
                if (eventType === 'REQUEST_USER_PROFILE') {
                    const url = window.location.href;
                    const auth = JSON.parse(JSON.parse(sessionStorage?.getItem('persist:member'))?.auth);
                    const accessToken = auth?.accessToken || '';
                    const isAuthenticated = auth?.isAuthenticated || false;
                    event.source.postMessage(
                        {
                            type: 'RESPONSE_USER_PROFILE',
                            payload: {
                                ...userInfo,
                                isAuthenticated,
                                accessToken,
                                sessionStorageRemindersData: getSessionStorageRemindersData,
                                brandId: brand?.id,
                                url,
                            },
                        },
                        trustedOrigin,
                    );
                // Close Drawer
                } else if (eventType === 'REQUEST_CLOSE_DRAWER') {
                    onClose();
                // Send Fresh Token to iFrame
                } else if (eventType === 'REQUEST_TOKEN_REFRESH') {
                    // Re-validate token
                    getAccessTokenSafely()
                        .then((token) => {
                            event.source.postMessage(
                                {
                                    type: 'RESPONSE_TOKEN_REFRESH',
                                    payload: token,
                                },
                                trustedOrigin,
                            );
                        })
                        .catch((error) => {
                            mbpLogger.logError({
                                appName: process.env.npm_package_name,
                                jsError: error,
                                module: 'RemindersDrawerContainer.js',
                                function: 'handleiFrameMessage',
                                message: 'Failed to refresh token on iFrame request',
                            });
                        });
                } else if (eventType === 'REQUEST_THEME') {
                    event.source.postMessage(
                        {
                            type: 'RESPONSE_THEME',
                            payload: JSON.stringify(theme),
                        },
                        trustedOrigin,
                    );
                } else if (eventType === 'REQUEST_AUTH_REDIRECT') {
                    const { userResponse,  guestRemindersAnywhereData } = event.data.payload;
                    handleAuthRedirect(
                        userResponse,
                        guestRemindersAnywhereData,
                    );
                } else if (eventType === 'REQUEST_CLEAR_REMINDERS_SESSION_DATA') {
                    if (window.sessionStorage) {
                        window.sessionStorage.removeItem('guestRemindersAnywhereData');
                    }
                } else if (eventType === 'REQUEST_TRACK_EVENT') {
                    const trackEventData = event.data.payload.trackEventData;
                    dispatch(trackEvent(trackEventData));
                }
            } catch (error) {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    jsError: error,
                    module: 'RemindersDrawerContainer.js',
                    function: 'handleiFrameMessage',
                    message: 'handleiFrameMessage could not post value to iFrame',
                });
            }
        };

        if (typeof window !== 'undefined' && isIframeLoaded) {
            window.addEventListener('message', handleiFrameMessage);
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('message', handleiFrameMessage);
            }
        };
    }, [isIframeLoaded, trustedOrigin, userInfo]);

    return isIframeLoaded ? (
        <MuiDrawer
            open
            onClose={onClose}
            disableScrollLock
            disableRestoreFocus
            anchor={ssrDeviceType === 'mobile' ? 'bottom' : 'right'}
            classes={{
                paper: classes.paper,
                root: classes.backdrop,
            }}
        >
            <iframe
                ref={iframeRef}
                id="engagement-occasions"
                title="engagement-occasions"
                className={classes.container}
                src={`${trustedOrigin}/engagement/reminders-container`}
                onLoad={() => setIsIframeLoaded(true)}
                onError={() => setIsIframeLoaded(false)}
            />
        </MuiDrawer>
    ) : null;
};

RemindersDrawerContainer.propTypes = {
    open: bool.isRequired,
    onClose: func.isRequired,
};

export default RemindersDrawerContainer;
